import { Building2, Shield, User, UserCheck } from 'lucide-react';

export interface ApiUserProfile {
  profile_id: string;
  profile_type: string;
  member_role: string | null;
  role: string;
  agent?: { id: string; territory_name: string | null };
  affiliate?: { id: string; name: string | null };
}

export interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: string;
  country_code: string | null;
  is_active: boolean;
  invite_status: 'invited' | 'active' | 'suspended';
  created_at: string;
  last_signed_in_at: string | null;
  // Profile-specific fields
  profile_id: string | null;
  profile_type: string | null;
  member_role: string | null;
  is_primary: boolean;
  // All profiles for this user
  profiles?: ApiUserProfile[];
  agent?: {
    id: string;
    territory_name: string | null;
  };
  affiliate?: {
    id: string;
    name: string | null;
  };
}

export interface UserProfile {
  id: string;
  profile_type: string;
  member_role: string | null;
  is_primary: boolean;
  is_active: boolean;
  affiliate_id: string | null;
  agent_id: string | null;
  individual_id: string | null;
  created_at: string;
  affiliate_name: string | null;
  territory_name: string | null;
}

export const profileTypeLabels: Record<string, string> = {
  hq_admin: 'HQ Admin',
  agent: 'Agent',
  affiliate_member: 'Affiliate Member',
  individual: 'Individual',
};

export const profileTypeIcons: Record<string, typeof Shield> = {
  hq_admin: Shield,
  agent: UserCheck,
  affiliate_member: Building2,
  individual: User,
};

export const profileTypeColors: Record<string, string> = {
  hq_admin: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  agent: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  affiliate_member: 'bg-green-500/10 text-green-500 border-green-500/20',
  individual: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

export const getProfileDisplayLabel = (profile: UserProfile): string => {
  if (profile.profile_type === 'hq_admin') return 'HQ Admin';
  if (profile.profile_type === 'agent') {
    return profile.territory_name ? `Agent: ${profile.territory_name}` : 'Agent';
  }
  if (profile.profile_type === 'affiliate_member') {
    const roleLabel =
      profile.member_role === 'owner'
        ? 'Owner'
        : profile.member_role === 'coach'
          ? 'Coach'
          : 'Member';
    const gymName = profile.affiliate_name || 'Unknown Gym';
    return `${roleLabel} @ ${gymName}`;
  }
  if (profile.profile_type === 'individual') return 'Individual';
  return profile.profile_type;
};

export const roleLabels: Record<string, string> = {
  hq_admin: 'HQ Admin',
  agent: 'Agent',
  affiliate_owner: 'Affiliate Owner',
  affiliate_coach: 'Affiliate Coach',
  member: 'Member',
  individual: 'Individual',
};

export const roleColors: Record<string, string> = {
  hq_admin: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  agent: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  affiliate_owner: 'bg-green-500/10 text-green-500 border-green-500/20',
  affiliate_coach: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
  member: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  individual: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

export const roleIcons: Record<string, typeof Shield> = {
  hq_admin: Shield,
  agent: UserCheck,
  affiliate_owner: Building2,
  affiliate_coach: Building2,
  member: User,
  individual: User,
};

// Helper to get profile label from a profile object
export const getProfileLabelFromProfile = (profile: ApiUserProfile): string => {
  if (profile.profile_type === 'affiliate_member') {
    const roleLabel =
      profile.member_role === 'owner'
        ? 'Owner'
        : profile.member_role === 'coach'
          ? 'Coach'
          : 'Member';
    const gymName = profile.affiliate?.name || 'Unknown Gym';
    return `${roleLabel} @ ${gymName}`;
  }
  if (profile.profile_type === 'agent' && profile.agent?.territory_name) {
    return `Agent: ${profile.agent.territory_name}`;
  }
  return roleLabels[profile.role] || profile.role;
};

// Helper to get profile label with context (fallback for users without profiles array)
export const getProfileLabel = (user: ApiUser): string => {
  if (!user.profile_type) {
    return roleLabels[user.role] || user.role;
  }

  if (user.profile_type === 'affiliate_member') {
    const roleLabel =
      user.member_role === 'owner' ? 'Owner' : user.member_role === 'coach' ? 'Coach' : 'Member';
    const gymName = user.affiliate?.name || 'Unknown Gym';
    return `${roleLabel} @ ${gymName}`;
  }

  if (user.profile_type === 'agent' && user.agent?.territory_name) {
    return `Agent: ${user.agent.territory_name}`;
  }

  return roleLabels[user.role] || user.role;
};
